import Vue                                from "vue";
import { mapGetters }                     from "vuex";
import { EntitlementsService, 
    AddressBookService }                  from "@/services/";
import { formatDateTime, stringHelper }   from "@/components/mixins";
import { FontAwesomeIcon }                from "@fortawesome/vue-fontawesome";
import moment                             from "moment";
import Datepicker                         from "vuejs-datepicker"

import { LoadingSpinner, 
    DnFormInput, 
    DnFunctionButton }                    from "@/components/Elements";

// Types
import { ICustomerAddressDto } from "@/types";

export default Vue.extend({
    name: "CcrRequestForm",
    components: {
        LoadingSpinner,
        DnFormInput,
        DnFunctionButton,
        FontAwesomeIcon,
        Datepicker
    },
    mixins: [formatDateTime, stringHelper],
    props:{
    },
    created() {
        window.scrollTo(0, 0);
        this.GetSubmissionEnabled();
        this.entitlements = this.getRecentComponentData.entitlements
        if( this.entitlements === undefined){
            this.$router.push({name: "Entitlements"})
        }
        this.dealer = this.getRecentComponentData.dealer
        this.SetupSubmissionObject()
        this.GetLastCcr();
        this.loadAddressBook();
    },
    computed: {
        ...mapGetters([
            "user",
            "activeAccountId",
            "getContentItem",
            "SiteName",
            "getRecentComponentData",
            "hasPermission"
        ]),
    },    
    data: () => ({
        isLoadingPreviousCcr: true,
        previousCcr: null,
        entitlements: [],
        dealer: {},
        dealerInfo: "",
        dealerContact: "",
        contactPhone: "",
        billOfLadingNumber: "",
        estimatedShipDate: Date,
        email: "",
        address: "",
        city: "",
        stateProvince: "",
        postalCode: "",
        returnRejectedCores: false,
        returnExtraCores: false,
        freightAccountNumber: "",
        freightCarrierMethod: "",
        notificationPreference: "No",
        notificationExceedsAmount: "",
        
        // todo: format date time
        dateFormats: {
            input: ['S', 'YYYY-MM-DD', 'YYYY/MM/DD'],
        },

        entitlementSubmission: [],
        isSubmittingRequest: false,
        formSubmissionErrors: [],
        isFormValid: true,
        
        // address book related
        addressBookList: [],
        customerAddressID: 0,
        savingNewCustomerAddress: false,
        showAddressSaveSuccess: false,
        showAddressSaveFailed: false,
        isSubmissionEnabledLoading: true,
        isCcrSubmissionEnabled: true
    }),
    methods: {
        async GetSubmissionEnabled(){
            EntitlementsService.IsCcrSubmissionEnabled()
            .then( response => {
                this.isCcrSubmissionEnabled = response.data;
                this.isSubmissionEnabledLoading = false;    
            });
        },
        IsSubmissionEnabled(){          
            return !this.isSubmissionEnabledLoading && (this.isCcrSubmissionEnabled || this.hasPermission("CcrSubmissionAdminOverride"));
        },
        GetLastCcr(){
           let requestParams = {
            AccountId: this.dealer?.AccountId === undefined ? this.activeAccountId : this.dealer.AccountId.toString(),
            PriorDaysToSearch: 90,
            OrderByField: "ModifiedDate",
            OrderByDirection: "DESC",
            PageNumber: 1,
            PageSize: 1
           } 
            
           EntitlementsService.GetCoreCreditRequests(requestParams)
           .then(response =>{

                this.dealerInfo = `${this.user.DealerCode}`
                this.dealerContact = `${this.user.FirstName} ${this.user.LastName}`
                this.contactPhone = this.user.Phone
                this.email = this.user.Email

                if( response.data === undefined || response.data.length === 0){
                    this.isLoadingPreviousCcr = false
                    this.estimatedShipDate = moment().toDate()
                    return                    
                }

                this.previousCcr = response.data[0].Ccr
                this.dealerInfo = `${this.user.DealerCode} - ${this.previousCcr.Account.Name}`


                // input fields
                this.billOfLadingNumber = this.previousCcr.BillOfLading
                this.estimatedShipDate =  this.previousCcr.ExpectedShipDate 
                this.address = this.previousCcr.Address
                this.city = this.previousCcr.City
                this.stateProvince = this.previousCcr.State
                this.postalCode = this.previousCcr.Zip
                
                // radio buttons
                this.returnRejectedCores = this.previousCcr.ReturnCore
                this.returnExtraCores = this.previousCcr.ReturnExtraCores
                this.notificationPreference = this.previousCcr.NotificationPreference

                this.isLoadingPreviousCcr = false
           })
           .catch(() => {
            this.$store.commit("pushTimedMessage", {
                header: "An error occurred retrieving your previous CCRs.",
                IsError: true
              })
            });   
        },
        SetupSubmissionObject(){
            if (!this.entitlements){
                return;
            } 

            this.entitlementSubmission = this.entitlements?.map(entitlement => ({
                Id: entitlement.Id,
                Entitlement: entitlement,
                ReturnQuantity: "1",
                Credit: "FullCredit",
                DealerReference: "",
                WarrantyClaimNumber: "",
                Type: ""
            }))
        },
        GetCreditRadioName(entitlement:any ){
            return `creditRadioButton_${entitlement.Id}`
        },
        SubmitCcrRequest(){
            this.setFormValidity()
            if( !this.isFormValid){
                return
            }

            let confirmMessage = "By clicking 'OK', you acknowledge that all cores are or will be available to ship by your expected ship date.\n\n"
            confirmMessage += "Please note that the address for the core processing center has changed to:\n\n"
            confirmMessage += "2950 Walkent Court NW\nGrand Rapids, MI 49544."
            if( !confirm(confirmMessage)){
                return
            }
            
            window.scroll(0,0)
            this.isSubmittingRequest = true
            this.ValidateCcrRequest()
            if( this.formSubmissionErrors.length > 0){
                this.isSubmittingRequest = false
            } else {
                let submissionRequest = {
                    AccountId: this.activeAccountId.toString() ,
                    UserId: this.user.UserId,
                    SiteName: this.SiteName,
                    CcrRequest: {
                        Address: this.address,
                        BillOfLading: this.billOfLadingNumber,
                        City: this.city,
                        ContactUser: this.dealerContact,
                        Email: this.email,
                        ExpectedShipDate: this.estimatedShipDate,
                        State: this.stateProvince,
                        Zip: this.postalCode,
                        ContactPhoneNumber: this.contactPhone,
                        ReturnCore: this.returnRejectedCores,
                        ReturnExtraCores: this.returnExtraCores,
                        FreightAccountNumber: this.freightAccountNumber,
                        FreightMethod: this.freightCarrierMethod,
                        NotificationPreference: this.notificationPreference,
                        NotificationExceedsAmount: this.notificationExceedsAmount,
                        Items: this.entitlementSubmission.map(item => ({
                                Id: item.Id,
                                Entitlement: item.Entitlement,
                                Quantity: item.ReturnQuantity,
                                CreditType: item.Credit,
                                ReferenceNumber: item.WarrantyClaimNumber === "" ? item.DealerReference : item.WarrantyClaimNumber,
                                Type: item.WarrantyClaimNumber === "" ? "Return" : "Warranty"
                        }))
                    }
                }
                
                let self = this
                EntitlementsService.SubmitCoreCreditRequest(submissionRequest).then(response => {
                    let ccrNumbers = response.data;
                    
                    if(ccrNumbers.length > 1){
                        this.$store.commit("pushMessage", {
                            header: `Success. Multiple new Core Credit Requests have been submitted. ${ccrNumbers.join()}`,
                            IsError: false,
                            seconds: 60
                          })
                        this.$router.push({name: "OpenCoreCreditRequests"})
                        return
                    }

                    let ccrNumber = ccrNumbers[0]
                    
                    let formattedEstimatedDate = typeof self.estimatedShipDate === "object" ? self.estimatedShipDate.toISOString() : self.estimatedShipDate
                    let accountId = self.activeAccountId;

                    this.$router.push({name: "CompleteCoreCreditRequest",  query: {ccrNumber: ccrNumber, accountId: accountId, estimatedShipDate: formattedEstimatedDate }})
                }).catch(err =>{
                    this.$store.commit("pushMessage", {
                        header: "There was an error submitting your core credit request.",
                        IsError: true
                      })
                }).finally(() => {
                    this.isSubmittingRequest = false;
                })
            }

        },
        setFormValidity(){
            this.isFormValid = true
            let warnings = document.getElementsByClassName('text-danger')
            let formWarnings = Array.from(warnings).filter( warning => {
                return warning.id != 'fullFormValidity'
            })
            this.isFormValid = formWarnings.length > 0 ? false : true
        },
        ValidateCcrRequest(){
            this.formSubmissionErrors = []

            for( const entitlementId in this.entitlementSubmission){
                let submittedEntitlement = this.entitlementSubmission[entitlementId]
                
                // Return Quantity
                let requestedReturnQuantity = parseInt(submittedEntitlement.ReturnQuantity)
                if( isNaN(requestedReturnQuantity)){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} return quantity is not a number.`)
                }

                if( requestedReturnQuantity < 1 ){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} is a quantity less than 1.`)
                }

                if( requestedReturnQuantity > submittedEntitlement.Entitlement.Quantity ){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} requested return quantity (${requestedReturnQuantity}), which is greater than entitlement quantity (${submittedEntitlement.Entitlement.Quantity}).`)
                } 

                // Credit Type
                if( submittedEntitlement.Credit !== "FullCredit" && submittedEntitlement.Credit !== "PartialCredit"){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} has no credit type selected.  Please select either 'Full' or 'Partial' credit.`)
                }

                // Dealer Reference
                if( submittedEntitlement.DealerReference.length > 30){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} has a Dealer Reference length longer than 30 characters. Please limit your Dealer Reference value to 30 or less characters (spaces included).`)
                }

                // Warranty Claim Number
                if( submittedEntitlement.WarrantyClaimNumber.length > 11 ){
                    this.formSubmissionErrors.push(`Part ${submittedEntitlement.Entitlement.OemPartCode} has an invalid warranty claim number. Please make sure the warranty claim number located in the 'Warranty Claim Number' box is less or equal to 11 characters in length.`)
                } 
            }

            // Freight Validation
            if( this.returnRejectedCores === "true" || this.returnExtraCores === "true"){
                if( this.freightAccountNumber === ""){
                    this.formSubmissionErrors.push("Please specify a Freight Collect account number.")
                }
                if( this.freightCarrierMethod === ""){
                    this.formSubmissionErrors.push("Please specify a Freight Collect method.")
                }
            }

            if( this.notificationPreference === "YesWhenExceedsAmount" && 
                (isNaN(parseFloat(this.notificationExceedsAmount)) || this.notificationPreference === "")){
                this.formSubmissionErrors.push("Please specify a notification amount.")
            }
        },

        async loadAddressBook() {
            let accountId = this.dealer?.AccountId === undefined ? this.activeAccountId : this.dealer.AccountId.toString();
            let userId = this.user.UserId;

            const response = await AddressBookService.GetAddressBookCustomers(
                accountId,
                userId,
                "",
                1,
                2000,
                "CustomerName",
                false
              );
        
              this.addressBookList = response.Items;
        },

        updateAddress() {
            const custAddressId = this.customerAddressID as number;

            if (custAddressId === 0) return;

            const chosenAddress = this.addressBookList.find((x) => x.AddressId === custAddressId);
 
            this.dealerContact = chosenAddress.CustomerName;
            this.address = chosenAddress.AddressLine1;
            this.city = chosenAddress.City;
            this.stateProvince = chosenAddress.StateProvince;
            this.postalCode = chosenAddress.PostalCode;
        },

        async saveAddress() {
            try {
              this.savingNewCustomerAddress = true;
      
              const addressDto :ICustomerAddressDto = {
                AccountId: this.activeAccountId,
                AddressId: 0,
                CustomerId: 0,
                CustomerName: this.dealerContact,
                CustomerName2: "",
                AddressLine1: this.address,
                AddressLine2: "",
                City: this.city,
                StateProvince: this.stateProvince,
                Country: "",
                PostalCode: this.postalCode
              };
      
              await AddressBookService.SaveCustomerAddress(
                this.activeAccountId,
                this.user.UserId,
                addressDto);
      
              var self = this;
              self.showAddressSaveSuccess = true;
              setTimeout(function(){
                self.showAddressSaveSuccess = false;
              }, 3000);
            }
            catch{
              var self = this;
              self.showAddressSaveFailed = true;
              setTimeout(function(){
                self.showAddressSaveFailed = false;
              }, 3000);
            } finally {
              this.savingNewCustomerAddress = false;
            }
        },
    }
});
